import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

export interface RecordingState {
  isRecording: boolean;
  recordingDuration: number;
  activeRecordingId: string | null;
  viewMode: "main" | "recording" | "after-record";
  audioSrc: string | null;
  hasActiveRecording: boolean;
  hasRestoredRecording: boolean;
  isAnalyserReady: boolean;
  duration: number;
}

const initialState: RecordingState = {
  isRecording: false,
  recordingDuration: 0,
  activeRecordingId: null,
  viewMode: "main",
  audioSrc: null,
  hasActiveRecording: false,
  hasRestoredRecording: false,
  isAnalyserReady: false,
  duration: 0,
};

const recordingSlice = createSlice({
  name: "recording",
  initialState,
  reducers: {
    startRecording: (state, action: PayloadAction<{ recordingId: string }>) => {
      state.isRecording = true;
      state.activeRecordingId = action.payload.recordingId;
      state.viewMode = "recording";
      state.recordingDuration = 0;
      state.isAnalyserReady = false;
    },
    stopRecording: (state, action: PayloadAction<{ audioUrl: string }>) => {
      state.isRecording = false;
      state.audioSrc = action.payload.audioUrl;
      state.viewMode = "after-record";
      state.isAnalyserReady = false;
      state.duration = state.recordingDuration;
    },
    setRecordingDuration: (state, action: PayloadAction<number>) => {
      state.recordingDuration = action.payload;
    },
    setDuration: (state, action: PayloadAction<number>) => {
      state.duration = action.payload;
    },
    setAudioSrc: (state, action: PayloadAction<string | null>) => {
      state.audioSrc = action.payload;
    },
    setViewMode: (state, action: PayloadAction<"main" | "recording" | "after-record">) => {
      state.viewMode = action.payload;
    },
    setActiveRecordingId: (state, action: PayloadAction<string | null>) => {
      state.activeRecordingId = action.payload;
    },
    setHasActiveRecording: (state, action: PayloadAction<boolean>) => {
      state.hasActiveRecording = action.payload;
    },
    setHasRestoredRecording: (state, action: PayloadAction<boolean>) => {
      state.hasRestoredRecording = action.payload;
    },
    setIsAnalyserReady: (state, action: PayloadAction<boolean>) => {
      state.isAnalyserReady = action.payload;
    },
    resetRecording: (state) => {
      state.isRecording = false;
      state.recordingDuration = 0;
      state.activeRecordingId = null;
      state.viewMode = "main";
      state.audioSrc = null;
      state.hasActiveRecording = false;
      state.hasRestoredRecording = false;
      state.isAnalyserReady = false;
      state.duration = 0;
    },
    restoreRecordingState: (
      state,
      action: PayloadAction<{
        recordingId: string;
        duration: number;
        audioUrl: string;
      }>
    ) => {
      state.activeRecordingId = action.payload.recordingId;
      state.hasActiveRecording = true;
      state.recordingDuration = action.payload.duration;
      state.duration = action.payload.duration;
      state.audioSrc = action.payload.audioUrl;
      state.viewMode = "after-record";
      state.hasRestoredRecording = true;
    },
  },
});

export const {
  startRecording,
  stopRecording,
  setRecordingDuration,
  setDuration,
  setAudioSrc,
  setViewMode,
  setActiveRecordingId,
  setHasActiveRecording,
  setHasRestoredRecording,
  setIsAnalyserReady,
  resetRecording,
  restoreRecordingState,
} = recordingSlice.actions;

export default recordingSlice.reducer;
